import { computed, Injectable, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectAvatar, selectUser, selectUserState } from '@libs/store/auth/selectors';
import { ENUM_USER_DATA_STATE, IPixKey, IUser, SIGN_PROVIDERS } from '@libs/interfaces';
import {
  addAvatarUrl,
  addGameBookmark,
  removeGameBookmark,
  setAvatarImage,
  setUser,
  setUserEmail,
  setUsername,
  updatePixKey,
  updateUserImageId,
} from '@libs/store/auth/actions';
import { filter, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserStoreService {
  user = signal<IUser>(null);
  username = computed<string>(() => this.user()?.data?.userName);
  email = computed<string>(() => this.user()?.data?.email);
  stateData = computed<ENUM_USER_DATA_STATE>(() => this.user()?.data?.state);
  userDataCompleted = computed<boolean>(() => this.stateData() === ENUM_USER_DATA_STATE.DATA_COMPLETED);
  userDocsCompleted = computed<boolean>(() => this.stateData() === ENUM_USER_DATA_STATE.DATA_DOCS_COMPLETED);
  isTelegramProviderAdded = computed<boolean>(() =>
    this.user()?.providers?.some(provider => provider.provider === SIGN_PROVIDERS.TELEGRAM),
  );
  isPhoneVerified = computed<boolean>(() => Boolean(this.user()?.verifications.find(v => v.phone?.length >= 10)));

  constructor(private store: Store) {
    this.selectUser()
      .pipe(
        filter(Boolean),
        tap((user: IUser) => this.user.set(user)),
      )
      .subscribe();
  }
  /**
   * Selects the user from the store.
   * @returns {Observable<IUser>} An observable containing the user data.
   */
  private selectUser(): Observable<IUser> {
    return this.store.select(selectUser);
  }

  /**
   * Updates the user's image ID.
   * @param {string} image_id - The new image ID.
   */
  updateUserImageId(image_id: string) {
    this.store.dispatch(updateUserImageId({ image_id }));
  }

  /**
   * Adds a new avatar URL.
   * @param {string} url - The URL of the new avatar.
   */
  addAvatarUrl(url: string) {
    this.store.dispatch(addAvatarUrl({ url }));
  }

  /**
   * Sets the avatar image.
   */
  setAvatarImage() {
    this.store.dispatch(setAvatarImage());
  }

  /**
   * Adds a game bookmark.
   * @param {string} game_id - The ID of the game to bookmark.
   */
  addGameBookmark(game_id: string) {
    this.store.dispatch(addGameBookmark({ id: game_id }));
  }

  /**
   * Removes a game bookmark.
   * @param {string} game_id - The ID of the game to remove from bookmarks.
   */
  removeGameBookmark(game_id: string) {
    this.store.dispatch(removeGameBookmark({ id: game_id }));
  }

  /**
   * Updates the user's Pix key.
   * @param {IPixKey} pixKey - The new Pix key.
   */
  updatePixKey(pixKey: IPixKey) {
    this.store.dispatch(updatePixKey({ pixKey }));
  }

  /**
   * Sets the username.
   * @param {string} username - The new username.
   */
  setUsername(username: string) {
    this.store.dispatch(setUsername({ username }));
  }

  /**
   * Sets the user data.
   * @param {IUser} user - The new user data.
   */
  setUser(user: IUser) {
    this.user.set(user);
    this.store.dispatch(setUser({ user }));
  }

  /**
   * Sets the user's email.
   * @param {string} email - The new email.
   */
  setUserEmail(email: string) {
    this.store.dispatch(setUserEmail({ email }));
  }

  /**
   * Selects the user state from the store.
   * @returns An observable of the user state.
   */
  selectUserState() {
    return this.store.select(selectUserState);
  }

  /**
   * Selects the avatar from the store.
   * @returns An observable of the avatar.
   */
  selectAvatar() {
    return this.store.select(selectAvatar);
  }
}
